import type { ImapSettings } from 'src/types/checkout';

import { z as zod } from 'zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';

const fldList = [
  { name: 'imapEnabled', required: false },
  { name: 'imapAddress', required: true },
  { name: 'imapPort', required: true },
  { name: 'imapLogin', required: true },
  { name: 'imapPassword', required: true },
  { name: 'imapEnableSsl', required: false },
];

export function useEmailIMAP() {
  const { t } = useTranslate('translation');
  const { handleIMAPDetails, state: channelState, onChangeStep } = useChannelContext();

  const ImapSettingSchema = validationSchema(fldList, t).superRefine((data, ctx) => {
    const imapData = data as ImapSettings;

    if (imapData.imapEnabled) {
      if (!imapData.imapAddress.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapAddress'],
          message: `${t('IMAP_ADDRESS')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!imapData.imapPort || imapData.imapPort <= 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPort'],
          message: t('PORT_POSITIVE_NUMBER'),
        });
      }

      if (!imapData.imapLogin.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapLogin'],
          message: `${t('IMAP_LOGIN')} ${t('IS_REQUIRED')}`,
        });
      }

      if (!imapData.imapPassword.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['imapPassword'],
          message: `${t('IMAP_PASSWORD')} ${t('IS_REQUIRED')}`,
        });
      }
    }
  });

  const methods = useForm<ImapSettings>({
    mode: 'all',
    resolver: zodResolver(ImapSettingSchema),
    defaultValues: channelState.imapDetails,
  });

  const { reset, handleSubmit, clearErrors, watch } = methods;

  const watchImapEnabled = watch('imapEnabled');

  const onSubmit = handleSubmit((data) => {
    if (!data.imapEnabled) {
      const defaultValObj = {
        imapAddress: '',
        imapPort: 0,
        imapLogin: '',
        imapPassword: '',
        imapEnableSsl: true,
      };
      handleIMAPDetails({ ...defaultValObj, imapEnabled: false });
      reset({ ...defaultValObj, imapEnabled: false });
    } else {
      handleIMAPDetails(data);
    }
    onChangeStep('next');
  });

  const btnDisableHandler = () => {
    if (watchImapEnabled) {
      const {
        imapAddress: address,
        imapPort: port,
        imapLogin: login,
        imapPassword: password,
      } = watch();
      return !address || !port || !login || !password;
    }
    return false;
  };

  useEffect(() => {
    if (!watchImapEnabled) clearErrors();
  }, [watchImapEnabled, clearErrors]);

  return {
    methods,
    onSubmit,
    watchImapEnabled,
    btnDisableHandler,
    t,
  };
}
